import { ALL_US_STATES_AND_TERRITORIES_MAP } from '@zg-rentals/states-territories';
import parsePhoneNumber from 'libphonenumber-js';

export const middleNameRegex = new RegExp(/^[A-Za-z]+$/);
export const middleNameErrorText = 'Only letters from the English alphabet are allowed';

export const firstLastNameRegex = new RegExp(/^[A-Za-z'-]+$/);
export const firstLastNameRegexErrorText =
  'Only letters from the English alphabet, hyphens, and apostrophes are allowed';

export const cityRegex = new RegExp(/^[A-Za-z ]{1,38}$/);
export const cityRegexErrorText = 'Only letters from the English alphabet and spaces are allowed';

export class MultiFieldValidator {
  private validators: Record<string, FieldValidator>;
  constructor() {
    this.validators = {};
  }

  add(fieldName: string, validator: FieldValidator) {
    this.validators[fieldName] = validator;
    return this;
  }

  isAllValid() {
    return Object.keys(this.validators).every((fieldName) => !this.validators[fieldName].build()?.length);
  }

  getErrors() {
    return Object.keys(this.validators).map((fieldName) => ({
      fieldName,
      errors: this.validators[fieldName].build(),
    }));
  }
}

export class FieldValidator {
  private value: string;
  private errors: Array<string>;
  constructor(value: string = '') {
    this.value = value?.trim();
    this.errors = [];
  }

  required(errorMsg = 'This field is required') {
    !this.value && this.errors.push(errorMsg);
    return this;
  }

  length(length: number, errorMsg = `Must be exactly ${length} characters long`) {
    if (!this.value) return this;

    this.value?.length !== length && this.errors.push(errorMsg);
    return this;
  }

  maxLength(length = 32, errorMsg = `Must be ${length} characters or less`) {
    this.value?.length > length && this.errors.push(errorMsg);
    return this;
  }

  regex(pattern: RegExp, errorMsg = 'Does not match expected pattern') {
    if (!this.value) return this;

    !pattern.test(this.value) && this.errors.push(errorMsg);
    return this;
  }

  isStateOrTerritory(errorMsg = 'Enter a valid US state or territory') {
    if (!this.value) return this;

    !Object.keys(ALL_US_STATES_AND_TERRITORIES_MAP).some((stateCode) => stateCode === this.value) &&
      this.errors.push(errorMsg);
    return this;
  }

  isUSPhoneNumber(errorMsg = 'Enter a valid US phone number') {
    if (!this.value) return this;

    const phone = parsePhoneNumber(this.value, 'US');

    (phone == null || !phone.isPossible() || !phone.isValid()) && this.errors.push(errorMsg);
    return this;
  }

  maxDate(
    date: Date,
    maxDate: Date,
    errorMsg = `Date must be on or before ${new Date(maxDate).toDateString().slice(4)}`,
  ) {
    if (!this.value) return this;

    if (date.getFullYear().toString().length !== 4) return this;

    date > maxDate && this.errors.push(errorMsg);
    return this;
  }

  minDate(
    date: Date,
    minDate: Date,
    errorMsg = `Date must be on or after ${new Date(minDate).toDateString().slice(4)}`,
  ) {
    if (!this.value) return this;

    if (date.getFullYear().toString().length !== 4) return this;

    date <= minDate && this.errors.push(errorMsg);
    return this;
  }

  isValid() {
    return !this.errors?.length;
  }

  build() {
    if (this.isValid()) {
      return null;
    }

    if (this.errors.length === 1) {
      // One line errors shouldn't end with a period
      // TODO: Find a way to check if a string fits on one line, for now, any single error
      // is considered a one-liner
      return this.errors[0];
    }

    return this.errors.join('. ') + '.';
  }
}
